import { ArrowRight } from "lucide-react";
import React, { useRef, useState } from "react";
import { View, StyleSheet, Pressable, Animated } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import { CourseInsightsRiskLevel } from "@axio/graphql/generated/graphql";
import { formatDateToSeason } from "../../../utils/date";

const statusConfig = {
  [CourseInsightsRiskLevel.AtRisk]: {
    borderColor: "#F0443833",
    backgroundColor: "#F044380D",
    hoverBorderColor: "#F0443866",
    indicatorColor: "#F04438",
    label: "Needs attention TODAY",
  },
  [CourseInsightsRiskLevel.Watch]: {
    borderColor: "#F7900933",
    backgroundColor: "#F790090D",
    hoverBorderColor: "#F7900966",
    indicatorColor: "#F79009",
    label: "Review this week",
  },
  [CourseInsightsRiskLevel.OnTrack]: {
    borderColor: "#12B76A33",
    backgroundColor: "#12B76A0D",
    hoverBorderColor: "#12B76A66",
    indicatorColor: "#12B76A",
    label: "On track",
  },
  draft: {
    borderColor: "#98A2B333",
    backgroundColor: "#98A2B30D",
    hoverBorderColor: "#98A2B366",
    indicatorColor: "#98A2B3",
    label: "Draft",
  },
};
const CourseHealthCard = ({ course, userId, onPress, atRisk, avgGrade }) => {
  const [isHovered, setIsHovered] = useState(false);
  const scale = useRef(new Animated.Value(1)).current;
  const translateY = useRef(new Animated.Value(0)).current;

  const handleHoverIn = () => {
    Animated.parallel([
      Animated.spring(scale, {
        toValue: 1.02,
        useNativeDriver: true,
      }),
      Animated.spring(translateY, {
        toValue: -4,
        useNativeDriver: true,
      }),
    ]).start();
    setIsHovered(true);
  };

  const handleHoverOut = () => {
    Animated.parallel([
      Animated.spring(scale, {
        toValue: 1,
        useNativeDriver: true,
      }),
      Animated.spring(translateY, {
        toValue: 0,
        useNativeDriver: true,
      }),
    ]).start();
    setIsHovered(false);
  };

  const handlePressIn = () => {
    Animated.spring(scale, {
      toValue: 0.98,
      useNativeDriver: true,
    }).start();
    onPress();
  };

  const handlePressOut = () => {
    Animated.spring(scale, {
      toValue: 1,
      useNativeDriver: true,
    }).start();
  };

  const shadowStyle = isHovered
    ? {
        shadowColor: "#000",
        shadowOffset: {
          width: 0,
          height: 12,
        },
        shadowOpacity: 0.12,
        shadowRadius: 16,
        elevation: 8,
      }
    : {};

  return (
    <Pressable
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}>
      <Animated.View
        style={[
          {
            transform: [{ scale: scale }, { translateY: translateY }],
            borderRadius: 32,
          },
          shadowStyle,
        ]}>
        <View
          style={[
            styles.card,
            {
              borderColor: isHovered ? statusConfig.draft.hoverBorderColor : statusConfig.draft.borderColor,
              minHeight: 164,
            },
            course.isDraft && styles.flexContainer,
          ]}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.titleContainer}>
              {course.isDraft && (
                <View style={[styles.indicator, { backgroundColor: statusConfig.draft.indicatorColor }]} />
              )}

              <View>
                <CustomText
                  text={course.name}
                  size="m"
                  useTranslationText={false}
                  textType="text"
                  weight="bold"
                  role="heading"
                  aria-level="1"
                  style={styles.titleText}
                />
                <CustomText
                  text={`${course?.course_code}  ·  ${formatDateToSeason(course?.created)}`}
                  size="s"
                  useTranslationText={false}
                  textType="text"
                  weight="normal"
                  role="heading"
                  aria-level="1"
                  style={{ color: "#344054", marginTop: 2 }}
                />
              </View>
            </View>
            <View>
              {course.isDraft && (
                <CustomText
                  text={statusConfig?.draft?.label}
                  size="xs"
                  useTranslationText={false}
                  textType="text"
                  weight="normal"
                  role="heading"
                  aria-level="1"
                  style={styles.draftLabel}
                />
              )}
            </View>
          </View>

          {/* Stats */}
          <View style={styles.footer}>
            {!course.isDraft ? (
              <View style={styles.statsContainer}>
                <View>
                  <CustomText
                    text="At-Risk"
                    size="xs"
                    useTranslationText={false}
                    textType="text"
                    weight="normal"
                    role="heading"
                    aria-level="1"
                    style={styles.titleHeader}
                  />
                  <CustomText
                    text={atRisk ? atRisk : 0}
                    size="xl"
                    useTranslationText={false}
                    textType="text"
                    weight="bold"
                    role="heading"
                    aria-level="1"
                    style={styles.titleText}
                  />
                </View>

                <View>
                  <CustomText
                    text="Enrolled"
                    size="xs"
                    useTranslationText={false}
                    textType="text"
                    weight="normal"
                    role="heading"
                    aria-level="1"
                    style={styles.titleHeader}
                  />
                  <CustomText
                    text={course?.enrolledStudents?.length || 0}
                    size="xl"
                    useTranslationText={false}
                    textType="text"
                    weight="bold"
                    role="heading"
                    aria-level="1"
                    style={styles.titleText}
                  />
                </View>

                <View>
                  <CustomText
                    text="Avg Grade"
                    size="xs"
                    useTranslationText={false}
                    textType="text"
                    weight="normal"
                    role="heading"
                    aria-level="1"
                    style={styles.titleHeader}
                  />
                  <CustomText
                    text={avgGrade ? avgGrade + "%" : "0%"}
                    size="xl"
                    useTranslationText={false}
                    textType="text"
                    weight="bold"
                    role="heading"
                    aria-level="1"
                    style={styles.titleText}
                  />
                </View>
              </View>
            ) : (
              <CustomText
                text="No students enrolled yet"
                size="s"
                useTranslationText={false}
                textType="text"
                weight="normal"
                role="heading"
                aria-level="1"
                style={{ color: "#667085", marginBottom: 12 }}
              />
            )}
            <ArrowRight size={16} color="#98A2B3" />
          </View>
        </View>
      </Animated.View>
    </Pressable>
  );
};
const styles = StyleSheet.create({
  flexContainer: { display: "flex", justifyContent: "center" },
  card: {
    width: "100%",
    backgroundColor: "rgba(255,255,255,0.6)",
    padding: 24,
    borderWidth: 1,
    borderRadius: 32,
    shadowColor: "#000",
    shadowOpacity: 0.08,
    shadowRadius: 10,
    elevation: 2,
  },

  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
  },

  titleContainer: {
    flexDirection: "row",
    gap: 12,
  },
  titleHeader: { color: "#667085", marginBottom: 4 },
  titleText: { color: "#101828" },
  indicator: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginTop: 6,
  },

  draftLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#98A2B3",
    borderRadius: 16,
    backgroundColor: "#98A2B30D",
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 2,
    paddingBottom: 2,
    color: "#98A2B3",
  },

  statsContainer: {
    flexDirection: "row",
    gap: 16,
  },

  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
});

export default CourseHealthCard;
